import React, { useState, forwardRef, useRef } from 'react';
import { Box, Typography, Button, Collapse, Tooltip } from '@mui/material';
import LoadingDots from '../LoadingDots/LoadingDots';
import RollingNumber from '../Loaders/RollingNumber';
import SkeletonLoaderWrapper from '../Loaders/SkeletonLoaderWrapper';

const getValueByPath = (obj, path) =>
  !obj || !path
    ? undefined
    : path
        .split('.')
        .reduce(
          (acc, part) => (acc && acc[part] != null ? acc[part] : undefined),
          obj
        );

const BuyIcon = 'https://assets.askhippo.co.za/Icons/common/Union.svg';

const InfoIcon = 'https://assets.askhippo.co.za/Icons/common/info.svg';

const GAP_COVER_TOOLTIP =
  'Gap cover helps pay the shortfall between what your medical aid covers and the actual cost of in-hospital treatment.';

const GapInfoTooltip = () => (
  <Tooltip
    title={<span>{GAP_COVER_TOOLTIP}</span>}
    placement="top"
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          fontFamily: 'Inter, sans-serif',
          fontWeight: 400,
          fontSize: '0.875rem',
          lineHeight: '1.25rem',
          letterSpacing: '-0.002em',
          borderRadius: '12px',
          border: '2px solid #E3E800',
          backgroundColor: '#FFFFFF',
          p: '16px',
          boxShadow: '0px 3px 8px #162F3B1F, 0px 1px 2px #A2ACB133',
          maxWidth: '297px',
          color: '#000',
          '& .MuiTooltip-arrow': { color: '#E3E800' },
        },
      },
      arrow: { sx: { color: '#E3E800' } },
    }}
  >
    <img
      src={InfoIcon}
      alt="Info"
      style={{ width: 16, height: 16, cursor: 'pointer' }}
    />
  </Tooltip>
);

const ensureRandPrefix = (val) => {
  if (val == null) return '';
  const s = String(val).trim();
  if (!s) return '';
  const numStr = s.replace(/^R?\s*/i, '').replace(/[\s,]/g, '');
  const n = Number(numStr);
  if (!isNaN(n) && numStr !== '') return `R ${n.toLocaleString('en-US')}`;
  return s.replace(/^R?\s*/i, 'R ');
};

function calcAnnualFromMonthlyInt(val) {
  const monthly = Number(val) || 0;
  const annual = monthly * 12;
  return ensureRandPrefix(String(annual));
}

// keeps spaces/words after the amount intact
const renderValueWithBoldCurrency = (text) => {
  if (text == null) return null;
  const str = String(text);
  const m = str.match(/(R\s?\d[\d\s.,]*)/i);
  if (!m) return str;
  const amount = m[1];
  const [head, tail] = str.split(amount);
  return (
    <>
      {head}
      <Box component="span" sx={{ fontWeight: 700 }}>
        {amount}
      </Box>
      {tail}
    </>
  );
};

const MobileProviderTileComponent_MedicalAid = forwardRef(
  (
    {
      data,
      initialLoadDone,
      onSelectQuote,
      onBuyOnline,
      bestDealContentReady = true,
      wasLoadingOnMount = useRef(!bestDealContentReady),
    },
    ref
  ) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const isBestDealLoading = data.bestDeal && !bestDealContentReady;
    const hasGap = (data.premium?.monthlypremium_gap ?? 0) > 0;
    const [openSection, setOpenSection] = useState(null); // index or null

    const [showChronicList, setShowChronicList] = useState(false);

    React.useEffect(() => {
      // find index of "Other Benefits"
      if (openSection !== null) return;
      setShowChronicList(false);
    }, [openSection]);

    const sections = React.useMemo(
      () => [
        {
          title: 'Hospital Cover',
          rows: [
            { label: 'Hospital Limit', key: 'hospitalcover.limit.description' },
            {
              label: 'Hospital Choice',
              key: 'hospitalcover.choice.description',
            },
            { label: 'Specialist Bills', key: 'hospitalcover.rate.moreinfo' },
            { label: 'Co-Payments', key: 'hospitalcover.choice.moreinfo' },
            {
              label: 'Hospital Exclusions',
              key: 'hospitalcover.exclusions.description',
            },
          ],
        },
        {
          title: 'Day-to-Day Cover',
          rows: [
            {
              label: 'Day to day Provider',
              key: 'daytodaycover.provider.description',
              belowValueKey: 'daytodaycover.provider.moreinfo',
            },
            { label: 'Day to day benefit', key: 'daytodaycover.moreinfo' },
            {
              label: 'Annual day to day',
              key: 'premium.monthlypremium_daytoday',
              transform: (v) => calcAnnualFromMonthlyInt(v),
              disableCurrencyBold: true,
            },
            { label: 'Summary', key: 'daytodaycover.generalinfo.summary.text' },
          ],
        },
        {
          title: 'Other Benefits',
          rows: [
            {
              label: 'Chronic medicine provider',
              key: 'chroniccover.providertype.description',
              belowValueKey: 'chroniccover.dsp.moreinfo',
            },
            {
              label: 'Chronic conditions',
              key: 'chroniccover.description',
              renderBelow: () => {
                const list = getValueByPath(data, 'chroniccover.conditions');
                const hasList = Array.isArray(list) && list.length > 0;
                return (
                  <Box sx={{ mt: '6px' }}>
                    {hasList && (
                      <>
                        <Button
                          variant="text"
                          onClick={() => setShowChronicList((s) => !s)}
                          sx={{
                            p: 0,
                            minWidth: 0,
                            textDecoration: 'underline',
                            textTransform: 'none',
                            fontSize: '0.875rem',
                            color: '#162F3B',
                          }}
                        >
                          {showChronicList ? 'Hide List' : 'Show Me'}
                        </Button>
                        <Collapse in={showChronicList} unmountOnExit>
                          <Box
                            component="ul"
                            sx={{ pl: '18px', mt: '6px', mb: 0 }}
                          >
                            {list.map((it, idx) => (
                              <li key={idx}>
                                <Typography
                                  sx={{
                                    fontFamily: 'Inter',
                                    fontWeight: 400,
                                    fontSize: '0.875rem',
                                    lineHeight: '1.275rem',
                                    color: '#162F3B',
                                  }}
                                >
                                  {String(it)}
                                </Typography>
                              </li>
                            ))}
                          </Box>
                        </Collapse>
                      </>
                    )}
                  </Box>
                );
              },
            },
            {
              label: 'Preventative care cover',
              key: 'additionalbenefits.preventativecare.level',
            },
            {
              label: 'Summary',
              key: 'additionalbenefits.preventativecare.description',
              disableCurrencyBold: true,
            },
          ],
        },
        {
          title: 'Cost Breakdown',
          rows: [
            {
              label: 'Risk',
              key: 'premium.monthlypremium_risk',
              transform: ensureRandPrefix,
              disableCurrencyBold: true,
            },
            {
              label: 'Day to day',
              key: 'premium.monthlypremium_daytoday',
              transform: ensureRandPrefix,
              disableCurrencyBold: true,
            },
            {
              label: 'Total Monthly Premium',
              key: 'premium.monthlypremium_total',
              transform: ensureRandPrefix,
              boldValue: true,
            },
          ],
        },
      ],
      [data, showChronicList]
    );

    const renderRow = ({ label, value, options = {} }) => {
      const { belowValue, boldValue, disableCurrencyBold, customBelowNode } =
        options;
      return (
        <Box
          key={label}
          sx={{
            backgroundColor: '#F6F6F6',
            border: '1px solid #E1E1E1',
            borderRadius: '12px',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            px: '16px',
            py: '12px',
            boxSizing: 'border-box',
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: '0.875rem',
              color: '#162F3B',
              mb: '4px',
            }}
          >
            {label}
          </Typography>
          <Typography
            sx={{
              fontWeight: boldValue ? 700 : 400,
              fontSize: '0.875rem',
              color: '#162F3B',
              textAlign: 'left',
              wordBreak: 'break-word',
              whiteSpace: 'normal',
            }}
          >
            {disableCurrencyBold
              ? String(value ?? '')
              : renderValueWithBoldCurrency(value)}
          </Typography>
          {belowValue && (
            <Typography
              sx={{ mt: '6px', fontSize: '0.8125rem', color: '#425869' }}
            >
              {String(belowValue)}
            </Typography>
          )}
          {customBelowNode}
        </Box>
      );
    };
    // case statement needed

    const handleSelectQuote = (evt) => {
      onSelectQuote(evt, { action: 'select-quote', data });
    };

    const handleBuyOnline = (evt) => {
      return onBuyOnline(evt, { action: 'buy-online', data });
    };
    return (
      <Box
        ref={ref}
        sx={{
          width: { xs: 'calc(100% - 32px)', sm: '100%' },
          margin: { xs: '0 16px 16px 16px', sm: '0 0 16px 0' },
          borderRadius: '22px',
          backgroundColor: '#FFFFFF',
          boxShadow: '0px 5px 15px 0px #00000026',
          border:
            data.bestDeal && initialLoadDone ? '1px solid #2DC8CB' : 'none',
          boxSizing: 'border-box',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column',
          padding: '16px',
          position: 'relative',
        }}
      >
        {data.bestDeal && initialLoadDone && (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0, // makes it stretch across the box’s full width
              height: '40px',
              backgroundColor: '#2DC8CB',
              borderTopLeftRadius: '18px',
              borderTopRightRadius: '18px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center', // center the text horizontally
              boxSizing: 'border-box',
              zIndex: 1, // ensure it stays above the rest
            }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1.25rem', // 28px
                lineHeight: '1.5rem',
                letterSpacing: '-0.02em',
                color: '#FFFFFF',
                textAlign: 'center',
              }}
            >
              {bestDealContentReady ? (
                'Your Best Deal!'
              ) : (
                <>
                  Calculating Your Best Deal <LoadingDots variant="buy-now" />
                </>
              )}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            padding: '16px',
            width: '100%',
            boxSizing: 'border-box',
            mt: data.bestDeal && initialLoadDone ? '40px' : 0,
          }}
        >
          {/* Expand/Collapse Button */}
          <Button
            onClick={() => setIsExpanded((prev) => !prev)}
            sx={{
              position: 'absolute',
              top: `${data.bestDeal && initialLoadDone ? 71 : 20}px`,
              right: '16px',
              width: '48px',
              height: '48px',
              padding: '8px',
              minWidth: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              boxShadow: 'none',
              '&:hover': { backgroundColor: 'transparent' },
            }}
          >
            <span
              className="material-symbols-rounded"
              style={{
                fontWeight: 500,
                fontSize: '1.5rem', // Same size as before
                color: '#000',
                transform: isExpanded ? 'rotate(180deg)' : 'none',
                transition: 'transform 0.3s ease',
                display: 'inline-block',
              }}
            >
              keyboard_arrow_down
            </span>
          </Button>

          {/* Logo / Image */}
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              justifyContent: 'left',
              alignItems: 'left',
            }}
          >
            <SkeletonLoaderWrapper loading={isBestDealLoading}>
              <img
                src={data.schemeinfo.logo.normal}
                alt={data.schemename}
                style={{
                  maxHeight: '48px',
                  maxWidth: '100%',
                  objectFit: 'contain',
                }}
              />
            </SkeletonLoaderWrapper>
          </Box>

          {/* Provider Name */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                mt: '12px',
                fontFamily: 'Inter',
                fontWeight: 700,
                fontSize: '1rem', // 16px
                color: '#1E354E',
              }}
            >
              {data.schemename}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Content */}
          <SkeletonLoaderWrapper loading={isBestDealLoading}>
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: '0.875rem', // 14px
                color: '#1E354E',
              }}
            >
              {data.optionname}
            </Typography>
          </SkeletonLoaderWrapper>

          {/* Divider */}
          <Box
            sx={{
              mt: '12px',
              width: '100%',
              height: '1px',
              backgroundColor: '#E0E0E0',
            }}
          />

          {/* Additional Info Pill */}
          {initialLoadDone &&
            Array.isArray(data.RetailChips) &&
            data.RetailChips.length > 0 && (
              <Box
                sx={{
                  mt: '12px',
                  backgroundColor: data.RetailChipColor,
                  borderRadius: '4px',
                  px: '0px',
                  py: '0px',
                  display: 'inline-block',
                }}
              >
                {data.RetailChips.map((chip, idx) => (
                  <Box key={idx}>
                    <SkeletonLoaderWrapper loading={isBestDealLoading}>
                      <Box
                        key={idx}
                        sx={{
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          color: '#162F3B',
                          backgroundColor: chip.color,
                          letterSpacing: '-0.02em',
                          lineHeight: '1.125rem',
                          borderRadius: '6px',
                          px: '8px',
                          py: '2px',
                          display: 'inline-block',
                        }}
                      >
                        {chip.text}
                      </Box>
                    </SkeletonLoaderWrapper>
                  </Box>
                ))}
              </Box>
            )}

          {/* Bottom Row: Premium/Excess on the left, CTA button on the right */}
          <Box
            sx={{
              mt:
                initialLoadDone &&
                Array.isArray(data.RetailChips) &&
                data.RetailChips.length > 0
                  ? '0px'
                  : '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'flex-end', // align bottom edges
              width: '100%',
            }}
          >
            {/* Premium and Excess */}
            <Box>
              <Typography
                sx={{
                  mt: '8px',
                  fontFamily: 'Inter',
                  fontWeight: 400,
                  fontSize: '1rem',
                  color: '#A4ACB0',
                }}
              >
                From
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                }}
              >
                <RollingNumber
                  value={
                    hasGap
                      ? data.premium.monthlypremium_medicalaid
                      : data.premium.monthlypremium_total
                  }
                  prefix={{
                    text: 'R',
                  }}
                  digitSx={{
                    fontFamily: 'Inter',
                    fontWeight: 700,
                    fontSize: '2rem',
                    color: '#162F3B',
                    letterSpacing: '-0.02em',
                  }}
                  suffix={{
                    text: 'pm',
                    sx: {
                      fontSize: '1.25rem',
                      fontWeight: 700,
                      ml: '0px',
                      color: '#162F3B',
                    },
                  }}
                  wrapperSx={{}}
                  extraLoops={3}
                  playSignal={
                    data.bestDeal && wasLoadingOnMount.current ? 1 : 0
                  }
                  animate={data.bestDeal && wasLoadingOnMount.current}
                />
              </Box>
              {hasGap && (
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '1rem',
                      lineHeight: '1.5rem',
                      letterSpacing: '-0.002em',
                      color: '#A2ACB1',
                    }}
                  >
                    R{' '}
                    {Number(data.premium.monthlypremium_total).toLocaleString(
                      'en-US'
                    )}{' '}
                    with Gap Cover
                  </Typography>
                  <GapInfoTooltip />
                </Box>
              )}
            </Box>

            {/* CTA Button (bottom aligned with Excess) */}
            <Button
              onClick={
                data.OnlinePremium && data.OnlinePremium < data.TotalPremium
                  ? handleBuyOnline
                  : handleSelectQuote
              }
              sx={{
                height: '40px',
                backgroundColor: '#FF0074',
                borderRadius: '18px',
                minWidth: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                px: '16px',
                py: '8px',
                '&:hover': { backgroundColor: '#e60067' },
                ...(isBestDealLoading && {
                  backgroundColor: '#D5D5D5 !important',
                  borderColor: '#D5D5D5 !important',
                  color: '#7A7A7A !important',
                  cursor: 'not-allowed',
                  boxShadow: 'none',
                  '&:hover': {
                    backgroundColor: '#D5D5D5 !important',
                  },
                }),
              }}
            >
              {initialLoadDone &&
              data.OnlinePremium &&
              data.OnlinePremium < data.TotalPremium ? (
                <>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 600,
                      fontSize: '1rem',
                      color: '#FFFFFF',
                      mr: '4px',
                    }}
                  >
                    Buy
                  </Typography>
                  <Box
                    component="img"
                    src={BuyIcon}
                    alt="BuyOnline"
                    sx={{
                      mx: '4px',
                      mb: '2px',
                      width: '18px',
                      height: '18px',
                    }}
                  />
                </>
              ) : (
                <span
                  className="material-symbols-rounded"
                  style={{ fontSize: '1.5rem', color: '#FFFFFF' }}
                >
                  double_arrow
                </span>
              )}
            </Button>
          </Box>

          {isExpanded && (
            <Box
              sx={{
                mt: '16px', // space above the expanded section
                display: 'flex',
                flexDirection: 'column',
                gap: '16px', // spacing between sections
              }}
            >
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* Description */}
              {data.schemedescription && (
                <Box>
                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 700,
                      fontSize: '1rem',
                      color: '#1E354E',
                    }}
                  >
                    Description
                  </Typography>

                  <Typography
                    sx={{
                      fontFamily: 'Inter',
                      fontWeight: 400,
                      fontSize: '1rem',
                      color: '#1E354E',
                      mt: '8px',
                    }}
                  >
                    {data.schemedescription}
                  </Typography>
                </Box>
              )}
              {/* Divider */}
              <Box
                sx={{
                  mt: '12px',
                  width: '100%',
                  height: '1px',
                  backgroundColor: '#E0E0E0',
                }}
              />
              {/* ===== 6) Bottom group: Benefits/Features | | Reviews ===== */}
              {/* Collapsible Sections (accordion) */}
              <Box
                sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}
              >
                {sections.map((section, idx) => {
                  // build rows for this section
                  const rows = section.rows
                    .map((r) => {
                      const raw = getValueByPath(data, r.key);
                      if (raw == null || String(raw).trim() === '') return null;
                      const transformed =
                        typeof r.transform === 'function'
                          ? r.transform(raw)
                          : raw;
                      const options = {
                        belowValue: r.belowValueKey
                          ? getValueByPath(data, r.belowValueKey)
                          : undefined,
                        boldValue: !!r.boldValue,
                        disableCurrencyBold: !!r.disableCurrencyBold,
                        customBelowNode:
                          typeof r.renderBelow === 'function'
                            ? r.renderBelow()
                            : undefined,
                      };
                      return { label: r.label, value: transformed, options };
                    })
                    .filter(Boolean);

                  if (rows.length === 0) return null;

                  const open = openSection === idx;

                  return (
                    <Box
                      key={section.title}
                      sx={{
                        border: '1px solid #E1E1E1',
                        borderRadius: '12px',
                        overflow: 'hidden',
                        backgroundColor: '#FFFFFF',
                      }}
                    >
                      {/* Header */}
                      <Button
                        onClick={() => setOpenSection(open ? null : idx)}
                        sx={{
                          width: '100%',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          textTransform: 'none',
                          px: '12px',
                          py: '10px',
                          borderRadius: 0,
                          backgroundColor: 'transparent',
                          '&:hover': { backgroundColor: '#F8F8F8' },
                        }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 700,
                            fontSize: '1rem',
                            color: '#1E354E',
                          }}
                        >
                          {section.title}
                        </Typography>
                        <span
                          className="material-symbols-rounded"
                          style={{
                            fontWeight: 500,
                            fontSize: '1.5rem',
                            color: '#000',
                            transform: open ? 'rotate(180deg)' : 'none',
                            transition: 'transform 0.2s ease',
                          }}
                        >
                          keyboard_arrow_down
                        </span>
                      </Button>

                      {/* Body */}
                      <Box
                        sx={{
                          maxHeight: open ? '2000px' : 0,
                          transition: 'max-height 0.25s ease',
                          overflow: 'hidden',
                          borderTop: '1px solid #EAEAEA',
                          px: '12px',
                          py: open ? '12px' : 0,
                          display: 'grid',
                          gridTemplateColumns: '1fr',
                          rowGap: '12px',
                        }}
                      >
                        {rows.map((row) => renderRow(row))}
                      </Box>
                    </Box>
                  );
                })}
              </Box>
            </Box>
          )}
        </Box>
      </Box>
    );
  }
);
MobileProviderTileComponent_MedicalAid.displayName =
  'MobileProviderTileComponent_MedicalAid';
export default MobileProviderTileComponent_MedicalAid;
